import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Link, Typography } from '@mui/material';

import { documentMetaLine, safeLegalUrl } from './legalUtils';

interface LegalDocumentViewProps {
  content: string;
  title?: string;
  effectiveDate?: string;
  // Hide the title/date header when the surrounding chrome already shows it.
  hideHeader?: boolean;
}

// No rehype-raw: raw HTML in the Markdown is rendered as literal text, never as markup.
// Images are dropped rather than fetched from wherever the author pointed them.
const components: Components = {
  a: ({ href, children }) => {
    const url = href ? safeLegalUrl(href) : '';
    if (!url) return <span>{children}</span>;
    return (
      <Link href={url} target="_blank" rel="noopener noreferrer" underline="always">
        {children}
      </Link>
    );
  },
  img: () => null,
  h1: ({ children }) => (
    <Typography variant="h4" component="h2" sx={{ mt: 3, mb: 1 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="h5" component="h3" sx={{ mt: 3, mb: 1 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="h6" component="h4" sx={{ mt: 2.5, mb: 1 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="subtitle1" component="h5" sx={{ mt: 2, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h5: ({ children }) => (
    <Typography variant="subtitle2" component="h6" sx={{ mt: 2, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h6: ({ children }) => (
    <Typography variant="subtitle2" component="h6" sx={{ mt: 2, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body1" component="p" sx={{ mb: 1.5, lineHeight: 1.65 }}>
      {children}
    </Typography>
  )
};

const LegalDocumentView: React.FC<LegalDocumentViewProps> = ({ content, title, effectiveDate, hideHeader = false }) => {
  const meta = documentMetaLine({ effectiveDate: effectiveDate || '' });

  return (
    <Box data-no-translate="true" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
      {!hideHeader && title && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h3" component="h1">
            {title}
          </Typography>
          {meta && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {meta}
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          '& > :first-of-type': { mt: 0 },
          '& ul, & ol': { pl: 3, mt: 0, mb: 1.5 },
          '& li': { mb: 0.5, lineHeight: 1.6 },
          '& li > p': { mb: 0.5 },
          '& blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary' },
          '& code': { fontFamily: 'monospace', fontSize: '0.9em', bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5 },
          '& pre': { bgcolor: 'grey.100', p: 1.5, borderRadius: 1, overflowX: 'auto', '& code': { bgcolor: 'transparent', p: 0 } },
          '& table': { borderCollapse: 'collapse', display: 'block', overflowX: 'auto', maxWidth: '100%', mb: 1.5 },
          '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left', verticalAlign: 'top' },
          '& th': { bgcolor: 'grey.100', fontWeight: 600 },
          '& hr': { border: 'none', borderTop: '1px solid', borderColor: 'divider', my: 2 }
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={safeLegalUrl} components={components}>
          {content || ''}
        </ReactMarkdown>
      </Box>
    </Box>
  );
};

export default LegalDocumentView;
